import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getDatabase, ref, set, push } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { recordActivity } from '../../../api/activityApi';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, MoreVertical } from 'lucide-react';
import './MaintenanceDraft.css';
import { ComponentLifetime, CostUnit, EditForm, getIntervalForDirection } from '../TemplatesEditor/types';
import { MaintenanceIntervalType } from '../../../types/Enums';
import { resolveCardinalDirection } from './utils/cardinalDirectionResolver';
import { BASE_TYPE_GROUPS } from '../../../Helpers/baseTypeUtils';
import LifetimeEditModal from '../TemplatesEditor/LifetimeEditModal';
import TemplateEditModal from '../TemplatesEditor/TemplateEditModal';
import { useUserNames } from '../../../Helpers/useUserNames';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { useIsWideViewport } from '../../../hooks/useIsWideViewport';

// Refactored imports - Types
import type {
    GroupedComponents,
    ComponentWithReplacement,
} from './types';

// Refactored imports - Utilities
import * as validation from './utils/validation';
import { syncDraftWithBuilding, newActivityEntry, addTemplateToDraft, applyTemplateIntervalChange, overridesLeftByTemplateChange, followTemplateInterval } from './utils/draftSync';
import type { TemplateOverridesLeft } from './utils/draftSync';
import { overdueToStartYear } from '../../../Helpers/maintenanceSchedule';
import { COST_SOURCE_LABELS, translateIntervalType } from '../../../Helpers/maintenanceCostUtils';
import type { MaintenanceActivity } from '../../../Helpers/maintenanceCostUtils';
import { processComponents as processComponentsUtil } from './utils/componentProcessing';
import { recalculateDatesFromCondition as recalculateDatesUtil } from './utils/draftRecalculation';
import { getInvalidComponents } from './utils/componentValidation';
import { getInvalidConfigTypes } from './utils/configValidation';
import { groupBySubsection, Subsection } from './utils/subsectionUtils';

// Refactored imports - Hooks
import { useDraftPersistence } from './hooks/useDraftPersistence';
import { useMaintenanceData } from './hooks/useMaintenanceData';
import { useModalManagement } from './hooks/useModalManagement';
import { useMaintenanceHandlers } from './hooks/useMaintenanceHandlers';
import { useDisplayHelpers } from './hooks/useDisplayHelpers';
import { useActivatePlan } from './hooks/useActivatePlan';
import type { ActivationAssessment } from './hooks/useActivatePlan';
import { liveComponentCountsByGroup } from '../../../Helpers/assessmentGroups';

// Refactored imports - Components
import { ConditionModal, CostSettingsModal, DeleteDraftModal, DraftPreviewModal, ReplacementCostModal, BulkApplySettingsModal, CopyActivitiesModal, MarkAllDoneModal, ActivatePlanModal, AddCheckModal, CheckDueDateModal } from './components/modals';
import type { CopyActivitiesSource } from './components/modals';
import { shownActivityIds, planActivityCopy } from './utils/copyActivities';
import { activityHasOwnValues, removedTypeTemplateIds } from './utils/removedActivities';
import ConfirmationModal from '../../../Components/ConfirmationModal';
import { EditTemplateCostModal } from './components/modals/EditTemplateCostModal';
import { EditTemplateActivityCostModal } from './components/modals/EditTemplateActivityCostModal';
import { EditActivityTemplateModal } from './components/modals/EditActivityTemplateModal';
import { IntervalSettingsModal } from './components/modals/IntervalSettingsModal';
import { AreaSettingsModal } from './components/modals/AreaSettingsModal';
import { TemplateSearchModal } from './components/modals/TemplateSearchModal';
import { MaintenanceTemplate } from '../TemplatesEditor/types';
import { useIntervalModal } from './hooks/useIntervalModal';
import { useAreaModal } from './hooks/useAreaModal';
import { ComplexShape } from '../../../types/GeometryShapes';
import { ValueStatus } from '../../../types/protocols';
import { formatDateForStorage } from '../../../Helpers/DateUtils';
import { SaveStatusBadge, ActionButtons, SubsectionedList } from './components/ui';
import { ComponentCard } from './components/ComponentCard';
import { ComponentCardProvider } from './contexts/ComponentCardContext';
import ComponentListItem from './components/ComponentListItem';
import { IndeterminateCheckbox } from './components/ui/IndeterminateCheckbox';
import { canSelect, keysToToggle, selectableKeysOf, selectionType } from './utils/bulkSelection';
import type { DraftEditMode } from './utils/bulkSelection';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import { BreadcrumbComponent } from '../../../Components/BreadCrumb';
import { ChevronDown } from 'lucide-react';
import { Collapse } from 'react-bootstrap';
import { useToast } from '../../../Components/ToastContext';
import { usePlanDiff } from './hooks/usePlanDiff';
import { DraftPreviewTabs } from './components/ui/DraftPreviewTabs';
import { useComponentChecks } from '../../../hooks/inspections/useComponentChecks';
import { useCheckName } from '../../../hooks/inspections/useCheckName';
import { useIsAdmin } from '../../../hooks/useIsAdmin';
import { checkRowsByTemplate, checksCoveringComponent, componentsOfBuilding, linkableTemplates } from '../../../Helpers/componentChecks';
import type { CheckRow, ComponentCheck } from '../../../Helpers/componentChecks';
import { isStandardComponentType } from '../../../Helpers/componentTypeOptions';
import type { BuildingCheckTemplate } from '../../BuildingChecks/types';
import BuildingCheckTemplateModal from '../../BuildingChecks/BuildingCheckTemplateModal';
import { Alert } from 'react-bootstrap';

// Interfaces moved to ./types.ts
// Utility functions moved to ./utils/ modules

const MaintenanceDraft: React.FC = () => {
    const { buildingKey } = useParams<{ buildingKey: string }>();
    const navigate = useNavigate();
    const { userNames } = useUserNames();
    const { types: componentTypeDefinitions, getLifetime, getTranslation, getAllTemplates, getTemplateById, getBaseType, refresh: refreshComponentTypes } = useBuildComponentTypes();
    const { showToast } = useToast();
    const isWideViewport = useIsWideViewport(2120);

    // Use maintenance data hook for all data loading
    // The hook manages state and exposes setters for optimistic updates after mutations
    const {
        building,
        componentLifetimes,
        maintenanceTemplates,
        loading,
        setBuilding,
        setComponentLifetimes,
        setMaintenanceTemplates
    } = useMaintenanceData(buildingKey);

    // Set document title
    useDocumentTitle(`Skapa underhållsplan - ${building?.summary?.name || 'Byggnad'}`);

    // Use draft persistence hook for draft state and autosave
    const {
        maintenanceDraft,
        setMaintenanceDraft,
        saveStatus,
        draftLoaded,
        loadDraft,
        scheduleAutosave
    } = useDraftPersistence(buildingKey);

    // Use activate plan hook
    const {
        activatePlan,
        activating,
        hasExistingPlan,
        checkingExistingPlan,
        activePlan
    } = useActivatePlan({
        buildingKey,
        maintenanceDraft,
        building,
        componentLifetimes,
        maintenanceTemplates
    });

    // Skickbedömning is set in the activation dialog, for the groups the building has live components in
    const assessmentGroupCounts = useMemo(
        () => liveComponentCountsByGroup(building, componentTypeDefinitions),
        [building, componentTypeDefinitions]
    );

    const [groupedComponents, setGroupedComponents] = useState<GroupedComponents>({});
    const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
    const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
    const [highlightedActivity, setHighlightedActivity] = useState<string | null>(null);
    const [dataFullyLoaded, setDataFullyLoaded] = useState<boolean>(false);
    const [selectedComponentKey, setSelectedComponentKey] = useState<string | null>(null);

    // Massredigering: the mode tab and the ticked components, in tick order. Session state only
    const [editMode, setEditMode] = useState<DraftEditMode>('single');
    const [bulkSelectedKeys, setBulkSelectedKeys] = useState<string[]>([]);

    // One year window for the preview, the diff tab and the activation review
    const [previewYears, setPreviewYears] = useState<number>(10);
    const planDiff = usePlanDiff({
        activePlan,
        draft: maintenanceDraft,
        building,
        componentLifetimes,
        maintenanceTemplates,
        yearsToShow: previewYears
    });
    // A component the draft no longer lists (gone from the building) has nothing to select
    const selectDiffComponent = useCallback((componentKey: string) => {
        if (!maintenanceDraft.components[componentKey]) return;
        setSelectedComponentKey(componentKey);
        setEditMode('single');
        setBulkSelectedKeys([]);
    }, [maintenanceDraft.components]);

    // Edit template cost modal state (replacement)
    const [showEditTemplateCostModal, setShowEditTemplateCostModal] = useState(false);
    const [editTemplateCostContext, setEditTemplateCostContext] = useState<{
        componentType: string;
        componentTypeName: string;
        templateCost: number;
        templateCostUnit: CostUnit;
    } | null>(null);

    // Edit template activity cost modal state (maintenance activities)
    const [showEditTemplateActivityCostModal, setShowEditTemplateActivityCostModal] = useState(false);
    const [editTemplateActivityCostContext, setEditTemplateActivityCostContext] = useState<{
        componentType: string;
        componentTypeName: string;
        templateId: string;
        templateName: string;
        templateCost: number;
        templateCostUnit: CostUnit;
    } | null>(null);

    // The template being edited from an activity row (admins only), with the row's components
    // (the selected ones in Massredigering): the ones asked about afterwards
    const [editingTemplate, setEditingTemplate] = useState<{ template: MaintenanceTemplate; componentKeys: string[] } | null>(null);
    const handleOpenEditTemplate = useCallback((template: MaintenanceTemplate, componentKeys: string[]): void => {
        setEditingTemplate({ template, componentKeys });
    }, []);
    // The edited template's rows among those components that kept their own interval or cost
    const [pendingTemplateFollow, setPendingTemplateFollow] = useState<{ template: MaintenanceTemplate; componentKeys: string[] } & TemplateOverridesLeft | null>(null);

    // Template search modal state
    const [showTemplateSearchModal, setShowTemplateSearchModal] = useState(false);
    const [newTemplateInitialName, setNewTemplateInitialName] = useState('');
    const [templateSearchComponentType, setTemplateSearchComponentType] = useState<string | null>(null);
    // The components the picked template is put on (every selected one in Massredigering)
    const [templateSearchComponentKeys, setTemplateSearchComponentKeys] = useState<string[]>([]);

    // Mark all done modal state
    const [showMarkAllDoneModal, setShowMarkAllDoneModal] = useState(false);

    // Activate plan modal state
    const [showActivateModal, setShowActivateModal] = useState(false);

    // Kopiera alla aktiviteter från annan komponent: the component being copied to
    const [copyActivitiesTargetKey, setCopyActivitiesTargetKey] = useState<string | null>(null);

    // Bulk apply menu state
    const [showBulkApplyMenu, setShowBulkApplyMenu] = useState(false);
    const bulkApplyMenuRef = useRef<HTMLDivElement>(null);

    // Close bulk apply menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (bulkApplyMenuRef.current && !bulkApplyMenuRef.current.contains(event.target as Node)) {
                setShowBulkApplyMenu(false);
            }
        };

        if (showBulkApplyMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showBulkApplyMenu]);

    // Track the components that need recalculation after building state updates
    const pendingRecalculationRef = useRef<string[] | null>(null);

    // Use maintenance handlers hook for all input change/blur handlers
    // NOTE: This must be called before useModalManagement since it provides cost handlers
    const {
        handleReplacementYearChange,
        handleReplacementYearBlur,
        handleReplacementCostChange,
        handleReplacementCostBlur,
        handleMaintenanceDateChange,
        handleMaintenanceDateBlur,
        handleMaintenanceIntervalChange,
        handleMaintenanceIntervalBlur,
        handleMaintenanceIntervalTypeChange,
        handleMaintenanceCostChange,
        handleMaintenanceCostBlur,
        handleMaintenanceActivityRemove,
        handleMaintenanceActivityAdd,
        handleDoneToggle,
        handleExcludeToggle,
        // Cost settings handlers
        handleActivityCostSettingsSave,
        handleReplacementCostSettingsSave,
        // Custom quantity handlers
        handleActivityQuantityChange,
        handleActivityQuantityClear,
        handleActivityUnitCountChange,
        handleActivityUnitCountClear,
        handleReplacementUnitCountChange,
        handleReplacementUnitCountClear,
        handleActivityCommentChange,
        // Bulk apply handler
        handleBulkApply,
        handleReplaceActivities
    } = useMaintenanceHandlers({
        maintenanceDraft,
        setMaintenanceDraft,
        scheduleAutosave,
        building,
        componentLifetimes,
        maintenanceTemplates,
        getTemplateById
    });

    // Use modal management hook for all modal state and handlers
    const {
        showDeleteModal,
        deleting,
        handleDeleteDraft,
        handleConfirmDeleteDraft,
        handleCancelDeleteDraft,
        showPreviewModal,
        handlePreview,
        handleClosePreview,
        showConditionModal,
        componentsForCondition,
        handleOpenConditionModal,
        handleCloseConditionModal,
        handleSaveCondition,
        showLifetimeModal,
        componentTypeForLifetime,
        savingLifetime,
        handleOpenLifetimeModal,
        handleCloseLifetimeModal,
        handleSaveLifetime,

        showNewActivityModal,
        componentTypeForNewActivity,
        savingNewActivity,
        handleOpenNewActivityModal,
        handleCloseNewActivityModal,
        handleSaveNewActivity,
        // Cost settings modal
        showCostSettingsModal,
        costSettingsContext,
        handleOpenCostSettingsModal,
        handleCloseCostSettingsModal,
        handleSaveCostSettings,
        // Replacement cost modal
        showReplacementCostModal,
        replacementCostContext,
        handleOpenReplacementCostModal,
        handleCloseReplacementCostModal,
        handleSaveReplacementCost,
        // Bulk apply modal
        showBulkApplyModal,
        bulkApplySourceKey,
        handleOpenBulkApplyModal,
        handleCloseBulkApplyModal
    } = useModalManagement({
        buildingKey,
        building,
        componentLifetimes,
        maintenanceTemplates,
        maintenanceDraft,
        groupedComponents,
        loading,
        setBuilding,
        pendingRecalculationRef,
        setMaintenanceTemplates,
        refreshComponentTypes,
        // Cost settings handlers
        handleActivityCostSettingsSave,
        handleReplacementCostSettingsSave
    });

    // Use interval modal hook for interval settings modal
    const {
        showIntervalModal,
        intervalContext,
        handleOpenIntervalModal,
        handleCloseIntervalModal,
        handleSaveInterval
    } = useIntervalModal({
        handleMaintenanceIntervalChange,
        handleMaintenanceIntervalBlur,
        handleMaintenanceIntervalTypeChange
    });

    // Handler for saving area to Firebase (used by useAreaModal)
    const handleSaveAreaToFirebase = useCallback(async (componentKey: string, newArea: number): Promise<void> => {
        if (!buildingKey) return;

        const auth = getAuth();
        const database = getDatabase();

        // Create a ComplexShape with the specified area. Firebase auto-ID keeps
        // shape keys consistent with the rest of the schema (iOS uses newKey()).
        const complexShape: ComplexShape = {
            key: push(ref(database)).key!,
            geometryType: 'complex',
            area: newArea
        };

        const areaShapesData = {
            value: [complexShape],
            updatedWhen: formatDateForStorage(new Date()),
            updatedBy: auth.currentUser?.uid || '',
            status: ValueStatus.set
        };

        await set(ref(database, `buildings/${buildingKey}/structure/components/${componentKey}/areaShapes`), areaShapesData);
        recordActivity('component.updated', { buildingKey, targetKey: componentKey });

        // Update local building state
        if (building?.model?.structure?.components?.[componentKey]) {
            const updatedBuilding = {
                ...building,
                model: {
                    ...building.model,
                    structure: {
                        ...building.model.structure,
                        components: {
                            ...building.model.structure.components,
                            [componentKey]: {
                                ...building.model.structure.components[componentKey],
                                areaShapes: areaShapesData
                            }
                        }
                    }
                }
            };
            setBuilding(updatedBuilding);
        }
    }, [buildingKey, building, setBuilding]);

    // Use area modal hook for area editing modal
    const {
        showAreaModal,
        areaContext,
        handleOpenAreaModal,
        handleCloseAreaModal,
        handleSaveArea
    } = useAreaModal({
        building,
        onSaveArea: handleSaveAreaToFirebase
    });

    // Use display helpers hook for all display/formatting functions
    const {
        getDisplayReplacementYear,
        getDisplayMaintenanceDate,
        getDisplayMaintenanceInterval,
        getDisplayBaseInterval,
        getDisplayLifetime,
        getDisplayReplacementCost,
        getDisplayMaintenanceCost,
        getReplacementCostExplanation,
        getMaintenanceCostExplanation,
        getRelevantTemplates
    } = useDisplayHelpers({
        maintenanceDraft,
        building,
        componentLifetimes,
        maintenanceTemplates,
        buildingKey,
        getTemplateById
    });

    // Modal handlers moved to useModalManagement hook
    // Wrapper for handleSaveLifetime to match LifetimeEditModal's expected signature
    const handleSaveLifetimeWrapper = async (lifetime: ComponentLifetime): Promise<void> => {
        await handleSaveLifetime(lifetime, setComponentLifetimes);
    };

    // A new activity is a template for the whole type: its rows need their entries now,
    // since the draft sync that would add them runs only once per building
    const handleSaveNewActivityWrapper = async (templateForm: EditForm): Promise<void> => {
        const template = await handleSaveNewActivity(templateForm);
        if (!template || !building) return;
        setMaintenanceDraft(prev => {
            const updatedDraft = addTemplateToDraft(prev, building, componentLifetimes, template);
            if (updatedDraft !== prev) scheduleAutosave(updatedDraft);
            return updatedDraft;
        });
    };

    // An edited template: rows still on its old interval follow the new one. The row the pencil
    // was clicked on (every selected one in Massredigering) is asked about when it kept its own
    // interval or cost, since the user changed the template while looking at it
    const handleTemplateSaved = (updated: MaintenanceTemplate): void => {
        const editing = editingTemplate;
        setEditingTemplate(null);
        if (!editing || !building) return;
        const before = editing.template;
        setMaintenanceDraft(prev => {
            const updatedDraft = applyTemplateIntervalChange(prev, building, before, updated);
            if (updatedDraft !== prev) scheduleAutosave(updatedDraft);
            return updatedDraft;
        });
        const left = overridesLeftByTemplateChange(maintenanceDraft, building, before, updated, editing.componentKeys);
        if (left.interval.length > 0 || left.cost.length > 0) {
            setPendingTemplateFollow({ template: updated, componentKeys: editing.componentKeys, ...left });
        }
    };

    const handleConfirmTemplateFollow = (): void => {
        const pending = pendingTemplateFollow;
        setPendingTemplateFollow(null);
        if (!pending || !building) return;
        const { template, interval, cost } = pending;
        if (interval.length > 0) {
            setMaintenanceDraft(prev => {
                const updatedDraft = followTemplateInterval(prev, building, template, interval);
                if (updatedDraft !== prev) scheduleAutosave(updatedDraft);
                return updatedDraft;
            });
        }
        // Back to schablon the way the cost modal does it: the own cost, its comment and attachments
        // go (the attachments are tombstoned), the investment share stays
        for (const componentKey of cost) {
            const activity = maintenanceDraft.components[componentKey]?.maintenanceActivities?.[template.id];
            handleActivityCostSettingsSave([componentKey], template.id, {
                costSource: 'template',
                comment: '',
                attachments: {},
                investmentPercentage: activity?.investmentPercentage ?? 0
            });
        }
    };

    // Template search modal handlers
    const handleOpenTemplateSearch = useCallback((componentType: string, componentKeys: string[]) => {
        setTemplateSearchComponentType(componentType);
        setTemplateSearchComponentKeys(componentKeys);
        setShowTemplateSearchModal(true);
    }, []);

    const handleCloseTemplateSearch = useCallback(() => {
        setShowTemplateSearchModal(false);
        setTemplateSearchComponentType(null);
        setTemplateSearchComponentKeys([]);
    }, []);

    const handleSelectTemplate = useCallback((template: MaintenanceTemplate) => {
        // The picked template becomes a switched-on row on the components that opened the search;
        // a row removed from one comes back fresh
        for (const componentKey of templateSearchComponentKeys) {
            handleMaintenanceActivityAdd(componentKey, template.id);
        }
        handleCloseTemplateSearch();
    }, [templateSearchComponentKeys, handleMaintenanceActivityAdd, handleCloseTemplateSearch]);

    // Removing an activity row is always confirmed (from every selected component in Massredigering)
    const [pendingActivityRemoval, setPendingActivityRemoval] = useState<{ componentKeys: string[]; templateId: string; isTypeTemplate: boolean } | null>(null);

    const handleRequestActivityRemoval = useCallback((componentKeys: string[], templateId: string, isTypeTemplate: boolean) => {
        setPendingActivityRemoval({ componentKeys, templateId, isTypeTemplate });
    }, []);

    const activityRemovalMessage = useMemo(() => {
        if (!pendingActivityRemoval) return '';
        const { componentKeys, templateId } = pendingActivityRemoval;
        const name = getTemplateById(templateId)?.name ?? 'Aktiviteten';
        const ownValues = componentKeys.some(componentKey => activityHasOwnValues(maintenanceDraft.components[componentKey]?.maintenanceActivities?.[templateId]))
            ? ' Aktivitetens egna inställningar (kostnad, kostnadskommentar, bilagor, area, investeringsandel eller aktivitetens kommentar) försvinner.'
            : '';
        const from = componentKeys.length === 1 ? 'komponenten' : `alla ${componentKeys.length} valda komponenter`;
        return `${name} tas bort från ${from}.${ownValues} Den kan läggas till igen med Ny aktivitet.`;
    }, [pendingActivityRemoval, getTemplateById, maintenanceDraft.components]);

    // Kontroller: building checks about the selected component. Live data, never part of the plan:
    // a check is written at once and nothing reaches the plan or its costs.
    const isAdmin = useIsAdmin();
    const componentChecks = useComponentChecks(buildingKey);
    // The components checks are being added for (every selected one in Massredigering); empty = no modal
    const [addCheckComponentKeys, setAddCheckComponentKeys] = useState<string[]>([]);
    const [addCheckInitialTemplate, setAddCheckInitialTemplate] = useState<BuildingCheckTemplate | null>(null);
    const [checkTemplateComponentKeys, setCheckTemplateComponentKeys] = useState<string[]>([]);
    const [pendingCheckRemoval, setPendingCheckRemoval] = useState<CheckRow | null>(null);
    const [checkActionBusy, setCheckActionBusy] = useState(false);

    const checkName = useCheckName(building);
    const checksForComponent = useCallback((componentKey: string) => checksCoveringComponent(
        componentKey,
        componentsOfBuilding(building),
        componentChecks.instances,
        componentChecks.templates,
        checkName
    ), [building, componentChecks.instances, componentChecks.templates, checkName]);

    // The checks about any of the components (each check is about one component)
    const checksForComponents = useCallback(
        (componentKeys: string[]): ComponentCheck[] => componentKeys.flatMap(componentKey => checksForComponent(componentKey)),
        [checksForComponent]
    );

    // Of the components, those no check of the template covers yet (a component is in at most one per template)
    const keysNotCoveredBy = useCallback((componentKeys: string[], templateId: string) =>
        componentKeys.filter(componentKey => !checksForComponent(componentKey).some(check => check.templateId === templateId)),
        [checksForComponent]);

    const selectedComponentChecks = useMemo(
        () => checkRowsByTemplate(selectedComponentKey ? checksForComponent(selectedComponentKey) : []),
        [selectedComponentKey, checksForComponent]
    );

    const handleOpenAddCheck = useCallback((componentKeys: string[]) => {
        setAddCheckInitialTemplate(null);
        setAddCheckComponentKeys(componentKeys);
    }, []);

    // Kontroller: the row whose next due date is being moved
    const [checkDateTarget, setCheckDateTarget] = useState<CheckRow | null>(null);

    const confirmCheckRemoval = async () => {
        if (!pendingCheckRemoval) return;
        setCheckActionBusy(true);
        await componentChecks.removeChecks(pendingCheckRemoval.checks.map(check => check.checkKey));
        setCheckActionBusy(false);
        setPendingCheckRemoval(null);
    };

    const handleCheckTemplateSaved = async (template: BuildingCheckTemplate) => {
        const componentKeys = checkTemplateComponentKeys;
        setCheckTemplateComponentKeys([]);
        await componentChecks.reload();
        // Continue with the new template: the building has no check for it yet, so ask for the first due date
        if (componentKeys.length > 0) {
            setAddCheckInitialTemplate(template);
            setAddCheckComponentKeys(componentKeys);
        }
    };

    // The type's own rows removed from any component the template search was opened for
    const templateSearchRemovedIds = useMemo(() => {
        if (templateSearchComponentKeys.length === 0 || !templateSearchComponentType) return [];
        const typeTemplateIds = getRelevantTemplates(templateSearchComponentType).map(t => t.id);
        const removed = new Set<string>();
        for (const componentKey of templateSearchComponentKeys) {
            removedTypeTemplateIds(maintenanceDraft.components[componentKey], typeTemplateIds).forEach(id => removed.add(id));
        }
        return Array.from(removed);
    }, [templateSearchComponentKeys, templateSearchComponentType, maintenanceDraft.components, getRelevantTemplates]);

    const handleCreateNewFromSearch = useCallback((searchTerm: string) => {
        // Close search modal and open the new template modal, with the search term as name
        if (templateSearchComponentType) {
            setNewTemplateInitialName(searchTerm);
            handleCloseTemplateSearch();
            handleOpenNewActivityModal(templateSearchComponentType);
        }
    }, [templateSearchComponentType, handleCloseTemplateSearch, handleOpenNewActivityModal]);

    // Close tooltip when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            setActiveTooltip(null);
        };

        if (activeTooltip) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [activeTooltip]);

    // Autosave timeout cleanup now handled in useDraftPersistence hook

    // Trigger pending recalculation when building state updates
    useEffect(() => {
        if (pendingRecalculationRef.current && building?.model?.structure?.components) {
            const componentKeys = pendingRecalculationRef.current;

            // Clear the pending flag
            pendingRecalculationRef.current = null;

            // Recalculate with the updated building state
            // The recalculation function will handle saving with the correct draft
            componentKeys.forEach(recalculateDatesFromCondition);
        }
    }, [building]);

    // Note: Data loading is now handled by useMaintenanceData hook

    // Load draft on mount or when buildingKey changes
    useEffect(() => {
        loadDraft();
    }, [buildingKey]);

    // Utility functions moved to ./utils/ modules
    // - syncDraftWithBuilding -> ./utils/draftSync (also creates the first draft)
    // - calculateReplacementDate -> ./utils/calculations
    // - calculateYearsRemaining -> ./utils/calculations
    // - calculateNextAssessmentDate -> ./utils/calculations

    // Process components and create maintenance plans
    // Process components using extracted utility
    const processComponents = (): void => {
        const grouped = processComponentsUtil(building, componentLifetimes);
        setGroupedComponents(grouped);
    };

    // Create a flat lookup map from groupedComponents for O(1) lookups
    const componentLookupMap = useMemo(() => {
        const map = new Map<string, ComponentWithReplacement>();
        for (const components of Object.values(groupedComponents)) {
            for (const component of components) {
                map.set(component.component.key, component);
            }
        }
        return map;
    }, [groupedComponents]);

    // The question after a template edit: names the component, or counts within the selection
    const templateFollowMessage = useMemo(() => {
        if (!pendingTemplateFollow || !building) return '';
        const { template, componentKeys, interval, cost } = pendingTemplateFollow;
        const total = componentKeys.length;
        const activityOf = (key: string) => maintenanceDraft.components[key]?.maintenanceActivities?.[template.id];
        const intervalText = (value: number | undefined, type: MaintenanceIntervalType | undefined) =>
            value && type ? `${value} ${translateIntervalType(type)}` : '';

        // The template's new interval for the rows' directions, when they share one
        const newIntervals = new Set(interval.map(key => {
            const component = componentLookupMap.get(key)?.component;
            const next = getIntervalForDirection(template, component ? resolveCardinalDirection(component, building) : null);
            return intervalText(next.interval_value, next.interval_type);
        }));
        const newInterval = newIntervals.size === 1 ? ` (${[...newIntervals][0]})` : ' för respektive väderstreck';

        const wantsBoth = interval.length > 0 && cost.length > 0;
        const asks = wantsBoth
            ? `mallens nya intervall${newInterval} och schablon`
            : interval.length > 0 ? `mallens nya intervall${newInterval}` : 'mallens nya schablon';
        const costNote = cost.length > 0 ? ' Den egna kostnaden med kommentar och bilagor tas bort.' : '';

        if (total === 1) {
            const key = componentKeys[0];
            const name = componentLookupMap.get(key)?.component.displayName(building) ?? 'Komponenten';
            const activity = activityOf(key);
            const has = [
                interval.length > 0 ? `ett anpassat intervall (${intervalText(activity?.intervalValue, activity?.intervalType)})` : '',
                cost.length > 0 ? `en egen kostnad (${COST_SOURCE_LABELS[activity?.costSource ?? 'template']})` : ''
            ].filter(Boolean).join(' och ');
            return `${name} har ${has} som inte följer mallen. Vill du använda ${asks} för komponenten?${costNote}`;
        }

        const count = (n: number) => n === total ? `alla ${total} valda komponenter` : `${n} av de ${total} valda komponenterna`;
        const has = wantsBoth
            ? `Av de ${total} valda komponenterna har ${interval.length} ett anpassat intervall och ${cost.length} en egen kostnad`
            : interval.length > 0
                ? `${count(interval.length)} har ett anpassat intervall`
                : `${count(cost.length)} har en egen kostnad`;
        const affected = new Set([...interval, ...cost]).size;
        const rest = affected < total ? ' Övriga valda komponenter följer redan mallen.' : '';
        const capitalised = has.charAt(0).toUpperCase() + has.slice(1);
        const these = affected === total ? 'alla' : affected === 1 ? 'den' : `dessa ${affected}`;
        return `${capitalised} som inte följer mallen. Vill du använda ${asks} för ${these}?${rest}${costNote}`;
    }, [pendingTemplateFollow, building, maintenanceDraft.components, componentLookupMap]);

    // Massredigering: the ticked components still in the draft, the specificType they lock the
    // selection to, and the rest of that type in the list
    const bulkPlans = useMemo(() => bulkSelectedKeys
        .map(key => componentLookupMap.get(key))
        .filter((plan): plan is ComponentWithReplacement => plan !== undefined && maintenanceDraft.components[plan.component.key] !== undefined),
    [bulkSelectedKeys, componentLookupMap, maintenanceDraft.components]);
    const bulkLockedType = useMemo(() => selectionType(bulkPlans), [bulkPlans]);
    const bulkSelectedSet = useMemo(() => new Set(bulkPlans.map(plan => plan.component.key)), [bulkPlans]);
    const unselectedKeysOfType = useMemo(() => {
        if (!bulkLockedType) return [];
        return Array.from(componentLookupMap.values())
            .filter(plan => plan.component.specificType?.value === bulkLockedType
                && maintenanceDraft.components[plan.component.key] !== undefined
                && !bulkSelectedSet.has(plan.component.key))
            .map(plan => plan.component.key);
    }, [bulkLockedType, componentLookupMap, maintenanceDraft.components, bulkSelectedSet]);
    const bulkChecks = useMemo(() => checkRowsByTemplate(checksForComponents(bulkPlans.map(plan => plan.component.key))), [bulkPlans, checksForComponents]);

    const toggleBulkKey = useCallback((componentKey: string) => {
        setBulkSelectedKeys(prev => (prev.includes(componentKey) ? prev.filter(key => key !== componentKey) : [...prev, componentKey]));
    }, []);
    const setBulkKeys = useCallback((componentKeys: string[], selected: boolean) => {
        setBulkSelectedKeys(prev => (selected
            ? [...prev, ...componentKeys.filter(key => !prev.includes(key))]
            : prev.filter(key => !componentKeys.includes(key))));
    }, []);

    // Enskild → Massredigering starts from the open component; back again opens the first ticked one
    const switchEditMode = useCallback((mode: DraftEditMode) => {
        if (mode === editMode) return;
        if (mode === 'multi') {
            const current = selectedComponentKey ? componentLookupMap.get(selectedComponentKey) : undefined;
            setBulkSelectedKeys(current && canSelect(current, null) ? [current.component.key] : []);
        } else {
            if (bulkPlans.length > 0) setSelectedComponentKey(bulkPlans[0].component.key);
            setBulkSelectedKeys([]);
        }
        setEditMode(mode);
    }, [editMode, selectedComponentKey, componentLookupMap, bulkPlans]);

    // Memoized statistics calculations for performance (must be before early returns)
    const draftStatistics = useMemo(() => {
        const componentKeys = Object.keys(maintenanceDraft.components);
        const totalComponents = componentKeys.length;

        const totalIncompleteComponents = componentKeys.filter(componentKey => {
            const componentPlan = componentLookupMap.get(componentKey);
            return componentPlan && (!componentPlan.hasCondition || !componentPlan.hasLifetime);
        }).length;

        const componentsNeedingReplacement = componentKeys.filter(componentKey => {
            const componentPlan = componentLookupMap.get(componentKey);
            return componentPlan && componentPlan.yearsRemaining !== null && componentPlan.yearsRemaining <= 5;
        }).length;

        // Check if all valid components are marked as done (excluding excluded components)
        const validComponents = componentKeys
            .map(componentKey => {
                const componentPlan = componentLookupMap.get(componentKey);
                if (!componentPlan) return null;
                // Include only components with condition and lifetime that are not excluded
                if (componentPlan.hasCondition && componentPlan.hasLifetime && maintenanceDraft.components[componentKey]?.excluded !== true) {
                    return componentPlan;
                }
                return null;
            })
            .filter((plan): plan is ComponentWithReplacement => plan !== null);

        const doneComponents = validComponents.filter(
            plan => maintenanceDraft.components[plan.component.key]?.isDone === true
        );

        const remainingComponents = validComponents.filter(
            plan => maintenanceDraft.components[plan.component.key]?.isDone !== true
        );

        const totalValidComponents = validComponents.length;
        const doneComponentsCount = doneComponents.length;
        // Treat sections with 0 valid components (all excluded) as done
        const allComponentsDone = totalValidComponents === 0 || (totalValidComponents > 0 && doneComponentsCount === totalValidComponents);

        return {
            totalComponents,
            totalIncompleteComponents,
            componentsNeedingReplacement,
            validComponents,
            doneComponentsCount,
            remainingComponents,
            totalValidComponents,
            allComponentsDone
        };
    }, [maintenanceDraft.components, componentLookupMap]);

    // Memoize subsections for each BASE_TYPE_GROUP
    const subsectionsByGroup = useMemo(() => {
        const result = new Map<string, Subsection[]>();
        for (const group of BASE_TYPE_GROUPS) {
            const components = group.baseTypes
                .flatMap(bt => groupedComponents[bt] || [])
                .filter(c => maintenanceDraft.components[c.component.key]);
            if (components.length === 0) continue;

            result.set(group.key, groupBySubsection(
                components, group.baseTypes, building, getTranslation
            ));
        }
        return result;
    }, [groupedComponents, maintenanceDraft.components, building, getTranslation]);

    // Calculate overall progress for the progress bar
    const overallProgress = useMemo(() => {
        // Get all components that are in the draft
        const allComponents = Object.values(groupedComponents).flat()
            .filter(c => maintenanceDraft.components[c.component.key]);

        // Valid components have condition and lifetime
        const validComponents = allComponents.filter(c => c.hasCondition && c.hasLifetime);

        // Processed = done or excluded
        const processedComponents = validComponents.filter(c => {
            const data = maintenanceDraft.components[c.component.key];
            return data?.isDone === true || data?.excluded === true;
        });

        return {
            total: validComponents.length,
            processed: processedComponents.length,
            percentage: validComponents.length > 0
                ? Math.round((processedComponents.length / validComponents.length) * 100)
                : 0
        };
    }, [groupedComponents, maintenanceDraft.components]);

    // Calculate remaining components count for mark all done modal
    const remainingComponentsCount = useMemo(() => {
        const allComponents = Object.values(groupedComponents).flat()
            .filter(c => maintenanceDraft.components[c.component.key]);

        return allComponents.filter(c => {
            if (!c.hasCondition || !c.hasLifetime) return false;
            const data = maintenanceDraft.components[c.component.key];
            return data?.isDone !== true && data?.excluded !== true;
        }).length;
    }, [groupedComponents, maintenanceDraft.components]);

    // Calculate excluded components count for activate modal
    const excludedComponentsCount = useMemo(() => {
        return Object.values(maintenanceDraft.components).filter(c => c.excluded === true).length;
    }, [maintenanceDraft.components]);

    // Calculate total components count in draft
    const totalComponentsCount = useMemo(() => {
        return Object.keys(maintenanceDraft.components).length;
    }, [maintenanceDraft.components]);

    // Calculate activation availability
    const canActivate = overallProgress.percentage === 100;
    const activateDisabledReason = useMemo(() => {
        // Activation replaces and archives the active plan: admins only (rule and API too)
        if (!isAdmin) return 'Endast administratörer kan aktivera underhållsplanen';
        if (saveStatus === 'saving') return 'Sparar ändringar...';
        if (saveStatus === 'error') return 'Osparade ändringar, försök uppdatera sidan';
        return undefined;
    }, [isAdmin, saveStatus]);

    // Handler to show activate confirmation modal
    const handleShowActivateModal = useCallback(() => {
        if (!canActivate) return;
        if (activateDisabledReason) {
            showToast(activateDisabledReason, 'warning');
            return;
        }
        setShowActivateModal(true);
    }, [canActivate, activateDisabledReason, showToast]);

    // Confirm handler for activation
    const confirmActivate = useCallback(async (assessment: ActivationAssessment) => {
        await activatePlan(assessment);
        setShowActivateModal(false);
    }, [activatePlan]);

    // Handler to show mark all done confirmation modal
    const handleMarkAllDone = useCallback(() => {
        if (remainingComponentsCount === 0) {
            showToast('Alla komponenter är redan behandlade', 'info');
            return;
        }
        setShowMarkAllDoneModal(true);
    }, [remainingComponentsCount, showToast]);

    // Confirm handler for marking all as done
    const confirmMarkAllDone = useCallback(() => {
        const allComponents = Object.values(groupedComponents).flat()
            .filter(c => maintenanceDraft.components[c.component.key]);

        const remainingComponents = allComponents.filter(c => {
            if (!c.hasCondition || !c.hasLifetime) return false;
            const data = maintenanceDraft.components[c.component.key];
            return data?.isDone !== true && data?.excluded !== true;
        });

        setMaintenanceDraft(prev => {
            const updatedComponents = { ...prev.components };

            for (const comp of remainingComponents) {
                const key = comp.component.key;
                updatedComponents[key] = {
                    ...updatedComponents[key],
                    isDone: true
                };
            }

            const updatedDraft = {
                ...prev,
                components: updatedComponents
            };

            scheduleAutosave(updatedDraft);
            return updatedDraft;
        });

        setShowMarkAllDoneModal(false);
        showToast(`${remainingComponents.length} komponenter markerade som klara`, 'success');
    }, [groupedComponents, maintenanceDraft.components, setMaintenanceDraft, scheduleAutosave, showToast]);

    // Compute bulk apply context: target components of the same specificType
    const bulkApplyContext = useMemo(() => {
        if (!bulkApplySourceKey || !selectedComponentKey) return null;

        // Find the source component
        const sourceComponent = componentLookupMap.get(bulkApplySourceKey);
        if (!sourceComponent) return null;

        const sourceSpecificType = sourceComponent.component.specificType?.value;
        if (!sourceSpecificType) return null;

        // Find all other components of the same specificType (excluding source)
        const targetComponents = Array.from(componentLookupMap.values())
            .filter(c =>
                c.component.key !== bulkApplySourceKey &&
                c.component.specificType?.value === sourceSpecificType
            );

        return {
            sourceComponent,
            targetComponents
        };
    }, [bulkApplySourceKey, selectedComponentKey, componentLookupMap]);

    // Kopiera alla aktiviteter från annan komponent: the other components of the same
    // base type that are in the plan and have activities, each with what
    // copying from it would do to the target
    const getCopyActivitiesSources = useCallback((targetKey: string): CopyActivitiesSource[] => {
        const target = componentLookupMap.get(targetKey);
        if (!target || !building) return [];

        const templateExists = (id: string) => getTemplateById(id) !== null;
        const sideOf = (plan: ComponentWithReplacement) => {
            const specificType = plan.component.specificType?.value;
            return {
                typeTemplateIds: specificType ? getRelevantTemplates(specificType).map(t => t.id) : [],
                activities: maintenanceDraft.components[plan.component.key]?.maintenanceActivities,
                removed: maintenanceDraft.components[plan.component.key]?.removedActivities
            };
        };
        // The row a component gets by default: what the draft sync would write for it
        const now = new Date();
        const defaultFor = (plan: ComponentWithReplacement) => (templateId: string): MaintenanceActivity => {
            const specificType = plan.component.specificType?.value;
            const fresh = newActivityEntry(
                plan.component,
                getTemplateById(templateId)!,
                specificType ? componentLifetimes[specificType] : undefined,
                building!,
                now
            );
            return { ...fresh, nextDate: overdueToStartYear(fresh.nextDate, now.getFullYear()) };
        };
        const targetSide = sideOf(target);
        const targetType = target.component.specificType?.value;
        const targetDefault = defaultFor(target);

        const sources: CopyActivitiesSource[] = [];
        componentLookupMap.forEach(candidate => {
            if (candidate.component.key === targetKey || candidate.component.type !== target.component.type) return;
            if (maintenanceDraft.components[candidate.component.key]?.excluded) return;

            const side = sideOf(candidate);
            const activityCount = shownActivityIds(side, templateExists).length;
            if (activityCount === 0) return;

            sources.push({
                component: candidate,
                activityCount,
                plan: planActivityCopy(side, targetSide, templateExists, defaultFor(candidate), targetDefault)
            });
        });

        // Same specific type first, then by type and name
        const typeRank = (source: CopyActivitiesSource) =>
            source.component.component.specificType?.value === targetType ? 0 : 1;
        const typeName = (source: CopyActivitiesSource) => getTranslation(source.component.component.specificType?.value ?? undefined);
        const name = (source: CopyActivitiesSource) => source.component.component.displayName(building);
        return sources.sort((x, y) =>
            typeRank(x) - typeRank(y)
            || typeName(x).localeCompare(typeName(y), 'sv')
            || name(x).localeCompare(name(y), 'sv')
        );
    }, [componentLookupMap, maintenanceDraft.components, getRelevantTemplates, getTemplateById, getTranslation, building, componentLifetimes]);

    const copyActivitiesSources = useMemo(
        () => selectedComponentKey ? getCopyActivitiesSources(selectedComponentKey) : [],
        [selectedComponentKey, getCopyActivitiesSources]
    );

    // Helper to check if current component has bulk apply targets
    const getHasBulkApplyTargets = useCallback((componentKey: string) => {
        const component = componentLookupMap.get(componentKey);
        if (!component) return false;

        const specificType = component.component.specificType?.value;
        if (!specificType) return false;

        // Check if there are other components of the same specificType
        return Array.from(componentLookupMap.values()).some(c =>
            c.component.key !== componentKey &&
            c.component.specificType?.value === specificType
        );
    }, [componentLookupMap]);

    const toggleSection = (sectionKey: string): void => {
        setCollapsedSections(prev => {
            const next = new Set(prev);
            if (next.has(sectionKey)) {
                next.delete(sectionKey);
            } else {
                next.add(sectionKey);
            }
            return next;
        });
    };

    useEffect(() => {
        if (!loading) {
            processComponents();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [building, componentLifetimes, loading]);

    // Validate components and redirect if any are invalid
    // This runs BEFORE draft initialization to prevent creating drafts for invalid buildings
    // ONLY runs for NEW drafts (empty), not when editing existing drafts
    useEffect(() => {
        if (!loading && building) {
            // Skip validation if we're editing an existing draft (has components)
            const isEditingExistingDraft = Object.keys(maintenanceDraft.components).length > 0;
            if (isEditingExistingDraft) {
                return;
            }

            const invalidComponents = getInvalidComponents(building);
            if (invalidComponents.length > 0) {
                navigate(`/building/${buildingKey}/validate-maintenance-components`);
                return;
            }

            // After component validation passes, validate config
            const invalidConfigTypes = getInvalidConfigTypes(building, getLifetime, getTranslation);
            if (invalidConfigTypes.length > 0) {
                navigate(`/building/${buildingKey}/validate-maintenance-config`);
            }
        }
    }, [loading, building, buildingKey, navigate, getLifetime, getTranslation, maintenanceDraft.components]);

    // Once everything for a building is loaded, bring the stored draft in line with
    // the building and the template config (an empty draft becomes the first draft).
    // Runs once per building: a sync on every draft change would refill a field the
    // user is in the middle of clearing. Skipped for a new draft when validation
    // fails, since the validation effect above is about to redirect.
    const syncedBuildingRef = useRef<string | null>(null);
    useEffect(() => {
        const isDataLoaded = validation.validateDataLoaded(building, componentLifetimes, maintenanceTemplates, maintenanceDraft, groupedComponents, loading);

        setDataFullyLoaded(isDataLoaded);

        if (!isDataLoaded || !draftLoaded || !building || !buildingKey) return;
        if (syncedBuildingRef.current === buildingKey) return;

        const isNewDraft = Object.keys(maintenanceDraft.components).length === 0;
        if (isNewDraft && (
            getInvalidComponents(building).length > 0 ||
            getInvalidConfigTypes(building, getLifetime, getTranslation).length > 0
        )) {
            return;
        }
        syncedBuildingRef.current = buildingKey;

        const result = syncDraftWithBuilding({
            draft: maintenanceDraft,
            building,
            componentLifetimes,
            maintenanceTemplates,
            findTemplate: id => getTemplateById(id) ?? undefined
        });
        if (!result.changed) return;

        setMaintenanceDraft(result.draft);
        scheduleAutosave(result.draft);

        if (!isNewDraft && (result.added.length > 0 || result.removed.length > 0)) {
            const parts: string[] = [];
            if (result.added.length > 0) parts.push(`${result.added.length} komponenter tillkom`);
            if (result.removed.length > 0) parts.push(`${result.removed.length} komponenter togs bort`);
            showToast(`Utkastet uppdaterades efter byggnaden: ${parts.join(', ')}`, 'info');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [building, componentLifetimes, maintenanceTemplates, maintenanceDraft.components, groupedComponents, loading, draftLoaded, buildingKey]);

    // Early return if buildingKey is not available (after all hooks)
    if (!buildingKey) {
        return (
            <div className="alert alert-warning">
                <h4>Byggnadsnyckel saknas</h4>
                <p>URL:en måste innehålla en byggnadsnyckel. Använd formatet: /maintenanceDrafts/byggnadsnyckel</p>
            </div>
        );
    }

    // Formatting functions moved to ./utils/formatting
    // - formatDate, formatYears, getConditionColor, getReplacementPriority, getAssessmentForGroup
    // Input handlers moved to useMaintenanceHandlers hook
    // Display helpers moved to useDisplayHelpers hook
    // Validation functions moved to utils/validation.ts
    // Helper functions (countDoneComponents, validateDataLoaded) moved to utils/validation.ts
    // calculateNextMaintenanceDate moved to utils/calculations.ts

    // Recalculate dates for a component after condition change (preserves all costs)
    // Recalculate dates using extracted utility
    const recalculateDatesFromCondition = (componentKey: string): void => {
        const updatedComponentData = recalculateDatesUtil(
            componentKey,
            building,
            componentLifetimes,
            maintenanceTemplates,
            maintenanceDraft
        );

        if (!updatedComponentData) return;

        // Update draft with recalculated data
        setMaintenanceDraft(prev => {
            const updatedDraft = {
                ...prev,
                components: {
                    ...prev.components,
                    [componentKey]: updatedComponentData
                }
            };

            // Schedule autosave with the updated draft to avoid race condition
            scheduleAutosave(updatedDraft);

            return updatedDraft;
        });
    };

    // Persistence functions (autosaveDraft, scheduleAutosave, loadDraft) now in useDraftPersistence hook
    // calculateInstallDate and generateTimelineActivities moved to utils and used by sub-components
    // renderComponentCard function removed - now using ComponentCard component

    if (loading) {
        return (
            <div className="loading-container">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                    <h5 className="text-muted">Laddar underhållsplan...</h5>
                </div>
            </div>
        );
    }

    if (!building) {
        return (
            <div className="alert alert-warning">
                <h4>Byggnad hittades inte</h4>
                <p>Kunde inte ladda byggnadsinformation för nyckel: {buildingKey!}</p>
            </div>
        );
    }

    // Destructure memoized statistics for use in JSX
    const {
        doneComponentsCount,
        remainingComponents,
        totalValidComponents,
        allComponentsDone
    } = draftStatistics;

    const buildingName = building.summary?.name || 'Byggnad';

    return (
        <div className="maintenance-draft">
            {/* Header Row: Breadcrumbs + Actions */}
            <div className="maintenance-draft-header">
                <BreadcrumbComponent
                    items={[
                        { text: buildingName, link: `/building/${buildingKey}` },
                        { text: 'Underhåll', link: `/maintenanceActive/${buildingKey}` },
                        { text: 'Utkast' }
                    ]}
                />
                <div className="maintenance-draft-header-actions">
                    <SaveStatusBadge status={saveStatus} />
                    <ActionButtons
                        onDelete={handleDeleteDraft}
                        onPreview={handlePreview}
                        onMarkAllDone={handleMarkAllDone}
                        showPreviewButton={!isWideViewport}
                        onActivate={handleShowActivateModal}
                        canActivate={canActivate}
                        activateDisabledReason={activateDisabledReason}
                    />
                </div>
            </div>

            {/* Rejection Comment */}
            {maintenanceDraft.rejectionComment && (
                <div className="rejection-comment-section">
                    <div className="alert alert-danger mb-0">
                        <div className="d-flex align-items-start">
                            <AlertCircle size={24} className="me-3 mt-1 text-danger flex-shrink-0" />
                            <div className="flex-grow-1">
                                <h5 className="alert-heading mb-2">
                                    Plan avvisad för granskning
                                </h5>
                                <div className="mb-2">
                                    <strong>Anledning:</strong>
                                </div>
                                <p className="mb-3" style={{ whiteSpace: 'pre-wrap' }}>
                                    {maintenanceDraft.rejectionComment.comment}
                                </p>
                                <div className="d-flex justify-content-between align-items-center text-muted">
                                    <small>
                                        <strong>Avvisad av:</strong> {userNames[maintenanceDraft.rejectionComment.rejectedBy] || 'Okänd användare'}
                                    </small>
                                    <small>
                                        <strong>Datum:</strong> {new Date(maintenanceDraft.rejectionComment.rejectedAt).toLocaleString('sv-SE')}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content - Split Pane Layout */}
            <div className="main-content-section">
                <div className="maintenance-draft-split-pane">
                    {/* Left Pane - Component List */}
                    <div className="maintenance-draft-left-pane">
                        {/* Enskild edits one component; Massredigering ticks several of one type and edits them at once */}
                        <div className="draft-mode-tabs" role="tablist" aria-label="Redigeringsläge">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={editMode === 'single'}
                                className={`draft-mode-tab ${editMode === 'single' ? 'active' : ''}`}
                                onClick={() => switchEditMode('single')}
                            >
                                Enskild
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={editMode === 'multi'}
                                className={`draft-mode-tab ${editMode === 'multi' ? 'active' : ''}`}
                                onClick={() => switchEditMode('multi')}
                            >
                                Massredigering
                                {editMode === 'multi' && bulkPlans.length > 0 && (
                                    <span className="draft-mode-tab-count">{bulkPlans.length}</span>
                                )}
                            </button>
                        </div>
                        <div className="maintenance-draft-list-container">
                            {BASE_TYPE_GROUPS.map((group) => {
                                // Combine components from all BaseTypes in this group
                                // FILTER: Only show components that are actually present in the draft snapshot
                                const components = group.baseTypes.flatMap(baseType => groupedComponents[baseType] || [])
                                    .filter(c => maintenanceDraft.components[c.component.key]);

                                if (components.length === 0) return null;

                                const sectionKey = `basetype-${group.key}`;
                                const isCollapsed = collapsedSections.has(sectionKey);

                                // Count done components (excluding excluded)
                                const includedComponents = components.filter(c => {
                                    const componentKey = c.component.key;
                                    return c.hasCondition && c.hasLifetime && maintenanceDraft.components[componentKey]?.excluded !== true;
                                });
                                const doneCount = validation.countDoneComponents(includedComponents, maintenanceDraft.components);
                                const totalCount = includedComponents.length;

                                // Check if all valid components are processed (done or excluded)
                                const validComponents = components.filter(c => c.hasCondition && c.hasLifetime);
                                const allProcessed = validComponents.length > 0 && validComponents.every(c => {
                                    const componentKey = c.component.key;
                                    const data = maintenanceDraft.components[componentKey];
                                    return data?.isDone === true || data?.excluded === true;
                                });

                                return (
                                    <div key={group.key} className="base-type-section">
                                        <div
                                            className={`base-type-header ${allProcessed ? 'all-processed' : ''}`}
                                            onClick={() => toggleSection(sectionKey)}
                                        >
                                            <ChevronDown
                                                size={16}
                                                className={`base-type-chevron ${isCollapsed ? 'collapsed' : ''}`}
                                            />
                                            <span className="base-type-header-name">{group.label}</span>
                                            <span className="base-type-header-count">
                                                {doneCount}/{totalCount}
                                            </span>
                                        </div>
                                        <Collapse in={!isCollapsed}>
                                            <div className="component-list">
                                                {(() => {
                                                    const subsections = subsectionsByGroup.get(group.key) || [];

                                                    const renderListItem = (plan: ComponentWithReplacement) => {
                                                        const componentKey = plan.component.key;
                                                        const isDone = maintenanceDraft.components[componentKey]?.isDone || false;
                                                        const isExcluded = maintenanceDraft.components[componentKey]?.excluded || false;
                                                        const hasIncompleteInfo = !plan.hasCondition || !plan.hasLifetime;

                                                        const isBulkMode = editMode === 'multi';

                                                        return (
                                                            <ComponentListItem
                                                                key={componentKey}
                                                                plan={plan}
                                                                building={building}
                                                                isSelected={isBulkMode ? bulkSelectedSet.has(componentKey) : selectedComponentKey === componentKey}
                                                                isDone={isDone}
                                                                isExcluded={isExcluded}
                                                                hasIncompleteInfo={hasIncompleteInfo}
                                                                onClick={() => setSelectedComponentKey(componentKey)}
                                                                selection={isBulkMode ? {
                                                                    checked: bulkSelectedSet.has(componentKey),
                                                                    disabled: !canSelect(plan, bulkLockedType),
                                                                    onToggle: () => toggleBulkKey(componentKey)
                                                                } : undefined}
                                                            />
                                                        );
                                                    };

                                                    if (subsections.length === 0) {
                                                        // No subsectioning - flat list
                                                        return components.map(renderListItem);
                                                    }

                                                    // Render with subsections
                                                    return (
                                                        <SubsectionedList
                                                            subsections={subsections}
                                                            renderItem={renderListItem}
                                                            draftComponents={maintenanceDraft.components}
                                                            selection={editMode === 'multi' ? {
                                                                selectableKeys: (subsectionComponents) => selectableKeysOf(subsectionComponents, bulkLockedType),
                                                                isSelected: (key) => bulkSelectedSet.has(key),
                                                                setSelected: setBulkKeys
                                                            } : undefined}
                                                        />
                                                    );
                                                })()}
                                            </div>
                                        </Collapse>
                                    </div>
                                );
                            })}

                            {Object.keys(maintenanceDraft.components).length === 0 && (
                                <div className="text-center text-muted p-4">
                                    <p>Inga komponenter registrerade</p>
                                </div>
                            )}
                        </div>

                        {/* Progress Bar */}
                        {overallProgress.total > 0 && (
                            <div className="draft-progress-bar-container">
                                <div className="draft-progress-info">
                                    <span className="draft-progress-label">Behandlade</span>
                                    <span className="draft-progress-count">
                                        {overallProgress.processed} / {overallProgress.total}
                                    </span>
                                </div>
                                <div className="draft-progress-bar">
                                    <div
                                        className="draft-progress-fill"
                                        style={{ width: `${overallProgress.percentage}%` }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Pane - Component Detail, or in Massredigering the bulk card for the ticked components */}
                    <div className="maintenance-draft-right-pane">
                        {(() => {
                            const isBulkMode = editMode === 'multi';
                            const cardPlans = isBulkMode
                                ? bulkPlans
                                : (selectedComponentKey && componentLookupMap.get(selectedComponentKey) ? [componentLookupMap.get(selectedComponentKey)!] : []);

                            if (cardPlans.length === 0) {
                                return (
                                    <div className="empty-state">
                                        <p>
                                            {isBulkMode
                                                ? 'Bocka för komponenter av samma typ i listan för att redigera dem tillsammans'
                                                : 'Välj en komponent från listan för att visa detaljer'}
                                        </p>
                                    </div>
                                );
                            }

                            const selectedPlan = cardPlans[0];
                            const cardKeys = cardPlans.map(plan => plan.component.key);
                            const primaryKey = selectedPlan.component.key;
                            const templates = selectedPlan.component.specificType?.value ? getRelevantTemplates(selectedPlan.component.specificType.value) : [];
                            const lifetime = selectedPlan.component.specificType?.value ? componentLifetimes[selectedPlan.component.specificType.value] : null;
                            const hasIncompleteInfo = cardPlans.some(plan => !plan.hasCondition || !plan.hasLifetime);
                            const excludedCount = cardKeys.filter(key => maintenanceDraft.components[key]?.excluded === true).length;
                            const isExcluded = excludedCount === cardKeys.length;

                            return (
                                <>
                                    {/* Action buttons above component card */}
                                    {!hasIncompleteInfo && (
                                        <div className="component-action-buttons">
                                            <label className={`exclude-checkbox-label ${isExcluded ? 'checked' : ''}`}>
                                                <IndeterminateCheckbox
                                                    className="form-check-input"
                                                    checked={isExcluded}
                                                    indeterminate={excludedCount > 0 && !isExcluded}
                                                    onChange={() => keysToToggle(cardKeys, maintenanceDraft.components, 'excluded').forEach(handleExcludeToggle)}
                                                />
                                                <span>
                                                    Exkludera från underhållsplan
                                                    {isBulkMode && excludedCount > 0 && !isExcluded && ` (${excludedCount} av ${cardKeys.length})`}
                                                </span>
                                            </label>
                                            {!isBulkMode && getHasBulkApplyTargets(primaryKey) && !isExcluded && (
                                                <div className="component-header-actions" ref={bulkApplyMenuRef}>
                                                    <button
                                                        className="component-actions-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowBulkApplyMenu(!showBulkApplyMenu);
                                                        }}
                                                        title="Fler alternativ"
                                                    >
                                                        <MoreVertical size={18} />
                                                    </button>
                                                    {showBulkApplyMenu && (
                                                        <div className="component-actions-menu">
                                                            <button onClick={() => {
                                                                setShowBulkApplyMenu(false);
                                                                handleOpenBulkApplyModal(primaryKey);
                                                            }}>
                                                                Kopiera inställningar till andra
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                {componentChecks.error && (
                                    <Alert variant="danger" dismissible onClose={componentChecks.clearError} className="mb-2">
                                        {componentChecks.error}
                                    </Alert>
                                )}
                                <ComponentCardProvider
                                    selectedPlan={selectedPlan}
                                    bulkPlans={isBulkMode ? cardPlans : undefined}
                                    buildingKey={buildingKey!}
                                    building={building}
                                    templates={templates}
                                    lifetime={lifetime}
                                    draftComponents={maintenanceDraft.components}
                                    getTemplateById={getTemplateById}
                                    highlightedActivity={highlightedActivity}
                                    setHighlightedActivity={setHighlightedActivity}
                                    activeTooltip={activeTooltip}
                                    setActiveTooltip={setActiveTooltip}
                                    handleOpenConditionModal={handleOpenConditionModal}
                                    handleOpenLifetimeModal={handleOpenLifetimeModal}
                                    handleOpenNewActivityModal={handleOpenTemplateSearch}
                                    handleReplacementYearChange={handleReplacementYearChange}
                                    handleReplacementYearBlur={handleReplacementYearBlur}
                                    handleReplacementCostChange={handleReplacementCostChange}
                                    handleReplacementCostBlur={handleReplacementCostBlur}
                                    handleMaintenanceActivityRemove={handleRequestActivityRemoval}
                                    handleMaintenanceActivityAdd={handleMaintenanceActivityAdd}
                                    handleMaintenanceIntervalChange={handleMaintenanceIntervalChange}
                                    handleMaintenanceIntervalBlur={handleMaintenanceIntervalBlur}
                                    handleMaintenanceIntervalTypeChange={handleMaintenanceIntervalTypeChange}
                                    handleMaintenanceDateChange={handleMaintenanceDateChange}
                                    handleMaintenanceDateBlur={handleMaintenanceDateBlur}
                                    handleMaintenanceCostChange={handleMaintenanceCostChange}
                                    handleMaintenanceCostBlur={handleMaintenanceCostBlur}
                                    handleDoneToggle={handleDoneToggle}
                                    handleExcludeToggle={handleExcludeToggle}
                                    handleOpenCostSettingsModal={handleOpenCostSettingsModal}
                                    handleOpenReplacementCostModal={handleOpenReplacementCostModal}
                                    handleOpenIntervalSettingsModal={handleOpenIntervalModal}
                                    handleOpenEditTemplateModal={isAdmin ? handleOpenEditTemplate : undefined}
                                    handleOpenAreaModal={handleOpenAreaModal}
                                    handleOpenBulkApplyModal={handleOpenBulkApplyModal}
                                    hasBulkApplyTargets={!isBulkMode && getHasBulkApplyTargets(primaryKey)}
                                    handleOpenCopyActivitiesModal={setCopyActivitiesTargetKey}
                                    hasCopyActivitiesSources={!isBulkMode && copyActivitiesSources.length > 0}
                                    coveringChecks={isBulkMode ? bulkChecks : selectedComponentChecks}
                                    handleOpenAddCheck={handleOpenAddCheck}
                                    handleRemoveCheck={isAdmin ? setPendingCheckRemoval : undefined}
                                    handleEditCheckDate={setCheckDateTarget}
                                    handleActivityQuantityChange={handleActivityQuantityChange}
                                    handleActivityQuantityClear={handleActivityQuantityClear}
                                    handleActivityUnitCountChange={handleActivityUnitCountChange}
                                    handleActivityUnitCountClear={handleActivityUnitCountClear}
                                    handleReplacementUnitCountChange={handleReplacementUnitCountChange}
                                    handleReplacementUnitCountClear={handleReplacementUnitCountClear}
                                    handleActivityCommentChange={handleActivityCommentChange}
                                    getDisplayReplacementYear={getDisplayReplacementYear}
                                    getDisplayReplacementCost={getDisplayReplacementCost}
                                    getDisplayLifetime={getDisplayLifetime}
                                    getDisplayMaintenanceInterval={getDisplayMaintenanceInterval}
                                    getDisplayMaintenanceDate={getDisplayMaintenanceDate}
                                    getDisplayMaintenanceCost={getDisplayMaintenanceCost}
                                    getDisplayBaseInterval={getDisplayBaseInterval}
                                    getReplacementCostExplanation={getReplacementCostExplanation}
                                    getMaintenanceCostExplanation={getMaintenanceCostExplanation}
                                >
                                    <ComponentCard
                                        bulkActions={isBulkMode ? {
                                            unselectedOfType: unselectedKeysOfType.length,
                                            onSelectAllOfType: () => setBulkKeys(unselectedKeysOfType, true),
                                            onClearSelection: () => setBulkSelectedKeys([])
                                        } : undefined}
                                    />
                                </ComponentCardProvider>
                                </>
                            );
                        })()}
                    </div>

                    {/* Preview Pane - Only on wide viewports */}
                    {isWideViewport && (
                        <div className="maintenance-draft-preview-pane">
                            <DraftPreviewTabs
                                building={building}
                                maintenanceDraft={maintenanceDraft}
                                componentLifetimes={componentLifetimes}
                                maintenanceTemplates={maintenanceTemplates}
                                showHeader={false}
                                activePlan={activePlan}
                                diff={planDiff}
                                yearsToShow={previewYears}
                                onYearsToShowChange={setPreviewYears}
                                onSelectComponent={selectDiffComponent}
                                selectedComponentKey={selectedComponentKey}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Draft Modal */}
            {showDeleteModal && (
                <DeleteDraftModal
                    onConfirm={handleConfirmDeleteDraft}
                    onCancel={handleCancelDeleteDraft}
                    deleting={deleting}
                />
            )}

            {/* Mark All Done Modal */}
            {showMarkAllDoneModal && (
                <MarkAllDoneModal
                    remainingCount={remainingComponentsCount}
                    onConfirm={confirmMarkAllDone}
                    onCancel={() => setShowMarkAllDoneModal(false)}
                />
            )}

            {/* Activate Plan Modal */}
            {showActivateModal && buildingKey && (
                <ActivatePlanModal
                    buildingKey={buildingKey}
                    assessmentGroupCounts={assessmentGroupCounts}
                    hasExistingPlan={hasExistingPlan}
                    excludedCount={excludedComponentsCount}
                    totalCount={totalComponentsCount}
                    onConfirm={confirmActivate}
                    onCancel={() => setShowActivateModal(false)}
                    activating={activating}
                    diff={planDiff}
                    yearsToShow={previewYears}
                    onYearsToShowChange={setPreviewYears}
                />
            )}

            {/* Preview Modal */}
            {showPreviewModal && building && maintenanceDraft && (
                <DraftPreviewModal
                    building={building}
                    maintenanceDraft={maintenanceDraft}
                    componentLifetimes={componentLifetimes}
                    maintenanceTemplates={maintenanceTemplates}
                    onClose={handleClosePreview}
                    activePlan={activePlan}
                    diff={planDiff}
                    yearsToShow={previewYears}
                    onYearsToShowChange={setPreviewYears}
                    onSelectComponent={selectDiffComponent}
                />
            )}

            {/* Condition Assessment Modal */}
            {showConditionModal && componentsForCondition.length > 0 && (
                <ConditionModal
                    component={componentsForCondition[0]}
                    bulkCount={componentsForCondition.length}
                    building={building}
                    onSave={handleSaveCondition}
                    onClose={handleCloseConditionModal}
                />
            )}

            {/* Lifetime Edit Modal */}
            {showLifetimeModal && componentTypeForLifetime && (
                <LifetimeEditModal
                    show={showLifetimeModal}
                    lifetime={componentLifetimes[componentTypeForLifetime]}
                    componentType={componentTypeForLifetime}
                    onSave={handleSaveLifetimeWrapper}
                    onCancel={handleCloseLifetimeModal}
                    saving={savingLifetime}
                />
            )}

            {/* Template Search Modal */}
            {showTemplateSearchModal && templateSearchComponentType && (
                <TemplateSearchModal
                    show={showTemplateSearchModal}
                    templates={getAllTemplates()}
                    currentComponentType={templateSearchComponentType}
                    currentBuildingKey={buildingKey}
                    removedTemplateIds={templateSearchRemovedIds}
                    getTranslation={getTranslation}
                    onSelectTemplate={handleSelectTemplate}
                    onCreateNew={handleCreateNewFromSearch}
                    onClose={handleCloseTemplateSearch}
                />
            )}

            {/* New Activity Modal (opens from search modal's "Create new" button) */}
            {showNewActivityModal && componentTypeForNewActivity && (
                <TemplateEditModal
                    show={showNewActivityModal}
                    template={null}
                    componentType={componentTypeForNewActivity}
                    componentTypeKey={componentTypeForNewActivity}
                    isNewTemplate={true}
                    buildingKey={buildingKey}
                    onSave={handleSaveNewActivityWrapper}
                    onCancel={handleCloseNewActivityModal}
                    saving={savingNewActivity}
                    infoText="Denna aktivitet kommer att läggas till på samtliga komponenter av denna typ i underhållsplanen"
                    componentTypeName={getTranslation(componentTypeForNewActivity)}
                    baseType={getBaseType(componentTypeForNewActivity)}
                    initialName={newTemplateInitialName}
                />
            )}

            {/* Cost Settings Modal */}
            {showCostSettingsModal && costSettingsContext && buildingKey && (
                <CostSettingsModal
                    componentKey={costSettingsContext.componentKey}
                    templateId={costSettingsContext.templateId}
                    templateName={costSettingsContext.templateName}
                    buildingKey={buildingKey}
                    activity={costSettingsContext.activity}
                    calculatedCost={costSettingsContext.calculatedCost}
                    quantity={costSettingsContext.quantity}
                    costExplanation={costSettingsContext.costExplanation}
                    componentType={costSettingsContext.componentType}
                    componentTypeName={costSettingsContext.componentTypeName}
                    templateCost={costSettingsContext.templateCost}
                    templateCostUnit={costSettingsContext.templateCostUnit}
                    canEditTemplateCost={costSettingsContext.componentType === costSettingsContext.templatePrimaryComponentType}
                    onSave={handleSaveCostSettings}
                    onClose={handleCloseCostSettingsModal}
                    onOpenEditTemplateCost={() => {
                        // Close the cost settings modal and open the template activity cost editor
                        handleCloseCostSettingsModal();
                        setEditTemplateActivityCostContext({
                            componentType: costSettingsContext.componentType,
                            componentTypeName: costSettingsContext.componentTypeName,
                            templateId: costSettingsContext.templateId,
                            templateName: costSettingsContext.templateName,
                            templateCost: costSettingsContext.templateCost,
                            templateCostUnit: costSettingsContext.templateCostUnit
                        });
                        setShowEditTemplateActivityCostModal(true);
                    }}
                />
            )}

            {/* Replacement Cost Modal */}
            {showReplacementCostModal && replacementCostContext && buildingKey && (
                <ReplacementCostModal
                    componentKey={replacementCostContext.componentKey}
                    componentName={replacementCostContext.componentName}
                    buildingKey={buildingKey}
                    componentData={replacementCostContext.componentData}
                    calculatedCost={replacementCostContext.calculatedCost}
                    quantity={replacementCostContext.quantity}
                    costExplanation={replacementCostContext.costExplanation}
                    componentType={replacementCostContext.componentType}
                    componentTypeName={replacementCostContext.componentTypeName}
                    templateCost={replacementCostContext.templateCost}
                    templateCostUnit={replacementCostContext.templateCostUnit}
                    onSave={handleSaveReplacementCost}
                    onClose={handleCloseReplacementCostModal}
                    onOpenEditTemplateCost={() => {
                        // Close the replacement cost modal and open the template cost editor
                        handleCloseReplacementCostModal();
                        setEditTemplateCostContext({
                            componentType: replacementCostContext.componentType,
                            componentTypeName: replacementCostContext.componentTypeName,
                            templateCost: replacementCostContext.templateCost,
                            templateCostUnit: replacementCostContext.templateCostUnit
                        });
                        setShowEditTemplateCostModal(true);
                    }}
                />
            )}

            {/* Edit Template Cost Modal (Replacement) */}
            {showEditTemplateCostModal && editTemplateCostContext && (
                <EditTemplateCostModal
                    componentType={editTemplateCostContext.componentType}
                    componentTypeName={editTemplateCostContext.componentTypeName}
                    currentCost={editTemplateCostContext.templateCost}
                    currentCostUnit={editTemplateCostContext.templateCostUnit}
                    buildingKey={buildingKey}
                    onClose={() => {
                        setShowEditTemplateCostModal(false);
                        setEditTemplateCostContext(null);
                    }}
                />
            )}

            {/* Edit Template Activity Cost Modal (Maintenance Activities) */}
            {showEditTemplateActivityCostModal && editTemplateActivityCostContext && (
                <EditTemplateActivityCostModal
                    componentType={editTemplateActivityCostContext.componentType}
                    componentTypeName={editTemplateActivityCostContext.componentTypeName}
                    templateId={editTemplateActivityCostContext.templateId}
                    templateName={editTemplateActivityCostContext.templateName}
                    currentCost={editTemplateActivityCostContext.templateCost}
                    currentCostUnit={editTemplateActivityCostContext.templateCostUnit}
                    buildingKey={buildingKey}
                    onClose={() => {
                        setShowEditTemplateActivityCostModal(false);
                        setEditTemplateActivityCostContext(null);
                    }}
                />
            )}

            {/* Edit Activity Template Modal (pencil next to the activity name) */}
            {editingTemplate && buildingKey && (
                <EditActivityTemplateModal
                    template={editingTemplate.template}
                    componentCount={editingTemplate.componentKeys.length}
                    buildingKey={buildingKey}
                    onSaved={handleTemplateSaved}
                    onClose={() => setEditingTemplate(null)}
                />
            )}

            {/* After a template edit: the row it was opened from kept its own interval or cost */}
            <ConfirmationModal
                show={pendingTemplateFollow !== null}
                title="Följ mallen?"
                message={templateFollowMessage}
                confirmText="Använd mallens"
                cancelText="Behåll anpassat"
                variant="primary"
                size="md"
                onConfirm={handleConfirmTemplateFollow}
                onCancel={() => setPendingTemplateFollow(null)}
            />

            {/* Interval Settings Modal */}
            {showIntervalModal && intervalContext && (
                <IntervalSettingsModal
                    key={`${intervalContext.componentKey}-${intervalContext.templateId}`}
                    context={intervalContext}
                    onSave={handleSaveInterval}
                    onClose={handleCloseIntervalModal}
                />
            )}

            {/* Area Settings Modal */}
            {showAreaModal && areaContext && (
                <AreaSettingsModal
                    key={areaContext.componentKey}
                    context={areaContext}
                    onSave={handleSaveArea}
                    onClose={handleCloseAreaModal}
                />
            )}

            {/* Bulk Apply Settings Modal */}
            {showBulkApplyModal && bulkApplyContext && building && (
                <BulkApplySettingsModal
                    sourceComponent={bulkApplyContext.sourceComponent}
                    targetComponents={bulkApplyContext.targetComponents}
                    building={building}
                    onApply={(targetKeys) => {
                        handleBulkApply(bulkApplySourceKey!, targetKeys);
                        handleCloseBulkApplyModal();
                        showToast(`Inställningar kopierade till ${targetKeys.length} komponenter`, 'success');
                    }}
                    onClose={handleCloseBulkApplyModal}
                />
            )}

            {/* Confirm removing an activity row */}
            <ConfirmationModal
                show={pendingActivityRemoval !== null}
                title="Ta bort aktivitet"
                message={activityRemovalMessage}
                size="md"
                onConfirm={() => {
                    if (pendingActivityRemoval) {
                        for (const componentKey of pendingActivityRemoval.componentKeys) {
                            handleMaintenanceActivityRemove(componentKey, pendingActivityRemoval.templateId, pendingActivityRemoval.isTypeTemplate);
                        }
                    }
                    setPendingActivityRemoval(null);
                }}
                onCancel={() => setPendingActivityRemoval(null)}
            />

            {/* Kontroller: move a check's next due date */}
            {checkDateTarget && (
                <CheckDueDateModal
                    row={checkDateTarget}
                    onSave={date => componentChecks.setNextDueDate(checkDateTarget.checks.map(check => check.checkKey), date)}
                    onClose={() => setCheckDateTarget(null)}
                />
            )}

            {/* Kontroller: one new check per component. The selected components a template
                already covers are skipped; a template covering every one of them is not offered */}
            {addCheckComponentKeys.length > 0 && building && addCheckComponentKeys.every(key => componentLookupMap.has(key)) && (() => {
                const covering = checksForComponents(addCheckComponentKeys);
                const coveringAll = covering.filter(check => keysNotCoveredBy(addCheckComponentKeys, check.templateId).length === 0);
                const componentName = addCheckComponentKeys.length === 1
                    ? componentLookupMap.get(addCheckComponentKeys[0])!.component.displayName(building)
                    : `${addCheckComponentKeys.length} komponenter`;
                return (
                    <AddCheckModal
                        componentName={componentName}
                        alreadyLinked={addCheckComponentKeys.length > 1
                            ? (templateId) => ({
                                linked: addCheckComponentKeys.length - keysNotCoveredBy(addCheckComponentKeys, templateId).length,
                                total: addCheckComponentKeys.length
                            })
                            : undefined}
                        templates={linkableTemplates(componentChecks.templates, coveringAll)}
                        instances={componentChecks.instances}
                        componentCount={addCheckComponentKeys.length}
                        canCreateTemplate={isAdmin}
                        initialTemplate={addCheckInitialTemplate}
                        onCreate={(template, settings) => componentChecks.createChecks(template, keysNotCoveredBy(addCheckComponentKeys, template.id), settings)}
                        onCreateTemplate={() => {
                            setCheckTemplateComponentKeys(addCheckComponentKeys);
                            setAddCheckComponentKeys([]);
                        }}
                        onClose={() => {
                            setAddCheckComponentKeys([]);
                            setAddCheckInitialTemplate(null);
                        }}
                    />
                );
            })()}

            {/* Kontroller: new check template (admins), then a check per component */}
            {checkTemplateComponentKeys.length > 0 && (
                <BuildingCheckTemplateModal
                    template={null}
                    excludeAssessment
                    initialSpecificType={(() => {
                        // The components' shared standard type, if they have one, is the suggestion
                        const typesOf = new Set(checkTemplateComponentKeys.map(key => componentLookupMap.get(key)?.component.specificType?.value));
                        const [only] = Array.from(typesOf);
                        return typesOf.size === 1 && only && isStandardComponentType(only) ? only : undefined;
                    })()}
                    infoText={checkTemplateComponentKeys.length === 1
                        ? 'När mallen är sparad skapas en kontroll för komponenten, med intervall och första förfallodatum som du väljer.'
                        : `När mallen är sparad skapas en kontroll för var och en av de ${checkTemplateComponentKeys.length} valda komponenterna, med intervall och första förfallodatum som du väljer.`}
                    onSaved={handleCheckTemplateSaved}
                    onCancel={() => setCheckTemplateComponentKeys([])}
                />
            )}

            {/* Kontroller: remove the row's checks from the building (admins) */}
            <ConfirmationModal
                show={pendingCheckRemoval !== null}
                title={pendingCheckRemoval && pendingCheckRemoval.checks.length > 1 ? 'Ta bort kontrollerna?' : 'Ta bort kontrollen?'}
                message={pendingCheckRemoval
                    ? (pendingCheckRemoval.checks.length === 1
                        ? `Kontrollen ${pendingCheckRemoval.name} tas bort från byggnaden. Utförda kontroller sparas.`
                        : `${pendingCheckRemoval.name}: kontrollerna på alla ${pendingCheckRemoval.checks.length} valda komponenter tas bort från byggnaden. Utförda kontroller sparas.`)
                    : ''}
                confirmText={pendingCheckRemoval && pendingCheckRemoval.checks.length > 1 ? 'Ta bort kontrollerna' : 'Ta bort kontroll'}
                size="md"
                loading={checkActionBusy}
                onConfirm={confirmCheckRemoval}
                onCancel={() => setPendingCheckRemoval(null)}
            />

            {/* Copy Activities Modal */}
            {copyActivitiesTargetKey && copyActivitiesTargetKey === selectedComponentKey && building
                && componentLookupMap.has(copyActivitiesTargetKey) && (
                <CopyActivitiesModal
                    targetComponent={componentLookupMap.get(copyActivitiesTargetKey)!}
                    sources={copyActivitiesSources}
                    building={building}
                    getTranslation={getTranslation}
                    getTemplateName={(id) => getTemplateById(id)?.name ?? id}
                    onApply={(source) => {
                        handleReplaceActivities(copyActivitiesTargetKey, source.plan.activities, source.plan.removedActivities);
                        setCopyActivitiesTargetKey(null);
                        showToast(`Aktiviteter kopierade från ${source.component.component.displayName(building)}`, 'success');
                    }}
                    onClose={() => setCopyActivitiesTargetKey(null)}
                />
            )}
        </div>
    );
};

export default MaintenanceDraft;
